import { Atom, ShieldCheck, Clock, FlaskConical, ChevronDown, MapPin, Award, MessageCircle, Menu, X } from "lucide-react";
import { lazy, Suspense, useState } from "react";
import heroAsset from "@/assets/hero-lab.webp.asset.json";
import logo from "@/assets/logo-glp.png";
import ScrollSection from "@/components/ScrollSection";
import CertificationBar from "@/components/CertificationBar";
import SEO from "@/components/SEO";
import { trackEvent } from "@/lib/analytics";

// Lazy-load everything below the fold to shrink the initial JS bundle.
const Services = lazy(() => import("@/components/Services"));
const MatricesSection = lazy(() => import("@/components/MatricesSection"));
const PainPoints = lazy(() => import("@/components/PainPoints"));
const SocialProof = lazy(() => import("@/components/SocialProof"));
const FAQ = lazy(() => import("@/components/FAQ"));
const ContactForm = lazy(() => import("@/components/ContactForm"));

const features = [
  { icon: Atom, title: "Especialistas em Radiação", desc: "Estrutura focada exclusivamente em ensaios de radiações ionizantes." },
  { icon: ShieldCheck, title: "Qualidade Acreditada", desc: "Processos acreditados garantindo os mais altos padrões de precisão analítica." },
  { icon: Clock, title: "Resultados Ágeis", desc: "Fluxos de trabalho eficientes entregando resultados quando você mais precisa." },
  { icon: FlaskConical, title: "Estrutura Completa", desc: "A mais completa estrutura análitica do setor de radiações ionizantes." },
  { icon: MapPin, title: "Localização Estratégica", desc: "Laboratório em Piracicaba/SP com infraestrutura de ponta." },
  { icon: Award, title: "Desde 2015", desc: "Anos de experiência e parcerias de sucesso com clientes de todo o Brasil." },
];

const Index = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  const navItems = [
    { href: "#matrices", label: "Matrizes" },
    { href: "#services", label: "Serviços" },
    { href: "#contact", label: "Contato" },
  ];

  const scrollToContact = (e: React.MouseEvent<HTMLAnchorElement>, location: string) => {
    e.preventDefault();
    trackEvent("cta_click", { location, label: "Enviar amostra para análise", channel: "form" });
    const target = document.getElementById("contact");
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    window.setTimeout(() => {
      const input = document.getElementById("contact-name") as HTMLInputElement | null;
      if (!input) return;
      if (isMobile) {
        input.classList.add("ring-2", "ring-primary", "ring-offset-2");
        window.setTimeout(() => {
          input.classList.remove("ring-2", "ring-primary", "ring-offset-2");
        }, 2000);
      } else {
        input.focus({ preventScroll: true });
      }
    }, 700);
  };

  return (
    <div className="min-h-dvh bg-background font-body">
      <SEO
        title="GLP Laboratórios - Ensaios de Radiações Ionizantes"
        description="Laboratório especializado em ensaios de radiações ionizantes. Espectrometria alfa, gama, cintilação líquida e contador proporcional. Piracicaba/SP."
        path="/"
      />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-card/80 backdrop-blur-md border-b border-border">
        <div className="container mx-auto flex items-center justify-between py-4 px-4">
          <img src={logo} alt="GLP Laboratórios" className="h-10" />
          <nav className="hidden md:flex items-center gap-6">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => trackEvent("nav_click", { location: "header_desktop", target: item.href })}
                className="text-foreground/80 hover:text-primary font-heading text-sm font-medium transition-colors rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                {item.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => trackEvent("cta_click", { location: "header_desktop", label: "Fale conosco", channel: "form" })}
              className="text-white font-heading font-semibold text-sm px-5 py-2.5 rounded-lg bg-[#25D366] transition-colors hover:bg-[#1FBA57] active:bg-[#1AA34C] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366] focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              Fale conosco
            </a>
          </nav>
          <div className="md:hidden flex items-center gap-2">
            <a
              href="https://wa.me/5519984559001"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("cta_click", { location: "header_mobile", label: "Fale conosco", channel: "whatsapp" })}
              className="inline-flex items-center gap-1.5 bg-[#25D366] text-white font-heading font-semibold text-sm px-3.5 py-2 rounded-lg transition-colors hover:bg-[#1FBA57] active:bg-[#1AA34C] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366] focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              <MessageCircle className="h-4 w-4" />
              Fale conosco
            </a>
            <button
            type="button"
            aria-label={menuOpen ? "Fechar menu" : "Abrir menu"}
            aria-expanded={menuOpen}
            onClick={() => {
              setMenuOpen((v) => {
                trackEvent("mobile_menu_toggle", { state: v ? "close" : "open" });
                return !v;
              });
            }}
              className="inline-flex items-center justify-center h-10 w-10 rounded-lg text-foreground transition-colors hover:bg-foreground/5 active:bg-foreground/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <nav className="md:hidden border-t border-border bg-card/95 backdrop-blur-md">
            <div className="container mx-auto px-4 py-4 flex flex-col gap-1">
              {navItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => {
                    trackEvent("nav_click", { location: "header_mobile_menu", target: item.href });
                    setMenuOpen(false);
                  }}
                  className="px-3 py-3 rounded-lg text-foreground font-heading font-medium transition-colors hover:bg-primary/10 hover:text-primary active:bg-primary/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  {item.label}
                </a>
              ))}
            </div>
          </nav>
        )}
      </header>

      <main id="main">
      {/* Hero */}
      <section className="relative pt-20">
        <div className="relative h-[calc(100svh-5rem)] min-h-[560px] max-h-[760px] sm:h-[70vh] sm:min-h-[500px] sm:max-h-[720px] overflow-hidden">
          <img
            src={heroAsset.url}
            alt="Laboratório de radiações ionizantes"
            className="absolute inset-0 w-full h-full object-cover object-[75%_center] sm:object-right"
            width={1920}
            height={821}
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/95 via-foreground/75 to-foreground/40 sm:bg-gradient-to-r sm:from-foreground/85 sm:via-foreground/55 sm:to-transparent" />
          <div className="absolute inset-0 flex items-center py-10 sm:py-0">
            <div className="container mx-auto px-4">
              <div className="max-w-xl mx-auto sm:mx-0 text-center sm:text-left">
                <h1 className="font-heading text-[26px] leading-[1.15] sm:text-4xl lg:text-5xl font-bold text-card">
                  Laudos radiométricos confiáveis para exportação, licenciamento e conformidade regulatória
                </h1>
                <p className="mt-4 text-base sm:text-lg text-card/80 max-w-md mx-auto sm:mx-0">
                  Ensaios acreditados ISO/IEC 17025 para água, solo, alimentos, mineração, resíduos e NORM.
                </p>
                <div className="mt-7 sm:mt-8 flex flex-col sm:flex-row gap-3">
                  <a
                    href="#contact"
                    onClick={(e) => scrollToContact(e, "hero")}
                    className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-heading font-semibold px-5 sm:px-6 py-3.5 sm:py-4 rounded-lg text-sm sm:text-base transition-colors hover:bg-primary/90 active:bg-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background w-full sm:w-auto text-center leading-tight"
                  >
                    <FlaskConical className="h-5 w-5 shrink-0" />
                    <span>Enviar amostra para análise</span>
                  </a>
                </div>
                <p className="mt-3 text-sm text-card/80 flex items-center justify-center sm:justify-start gap-1.5">
                  <Clock className="h-4 w-4" />
                  Resposta em até 15 minutos úteis
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="hidden sm:block absolute bottom-6 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="h-6 w-6 text-card/60" />
        </div>
      </section>

      {/* Certification Bar */}
      <CertificationBar />

      {/* Features */}
      <ScrollSection as="section" className="py-20 bg-card">
        <div className="container mx-auto px-4">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground text-center">
            Por que escolher a GLP
          </h2>
          <p className="text-muted-foreground text-center mt-3 max-w-lg mx-auto">
            Desde 2015 em Piracicaba, combinamos tecnologia de ponta com experiência para entregar resultados em que você pode confiar.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-14">
            {features.map((f) => (
              <div key={f.title} className="group text-center p-6 rounded-2xl border border-border hover:border-primary/30 hover:shadow-lg transition-all duration-300">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 text-primary mb-5 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <f.icon className="h-7 w-7" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-foreground">{f.title}</h3>
                <p className="text-muted-foreground text-sm mt-2 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </ScrollSection>

      <Suspense fallback={<div className="min-h-[200px]" />}>
        {/* Analytical Techniques (Services) */}
        <ScrollSection>
          <Services />
        </ScrollSection>

        {/* Pain Points */}
        <ScrollSection>
          <PainPoints />
        </ScrollSection>

        {/* Matrices */}
        <ScrollSection>
          <MatricesSection />
        </ScrollSection>
      </Suspense>

      {/* CTA: Sample */}
      <ScrollSection as="section" className="py-16 bg-primary/5">
        <div className="container mx-auto px-4 text-center">
          <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-4">
            Tenho uma amostra para analisar
          </h3>
          <p className="text-muted-foreground max-w-lg mx-auto mb-8">
            Fale direto com nosso time no WhatsApp ou envie sua amostra para avaliação.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/5519984559001"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("cta_click", { location: "matrices_cta", label: "Falar no WhatsApp", channel: "whatsapp" })}
              className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-heading font-semibold px-8 py-4 rounded-lg text-base transition-colors hover:bg-[#1FBA57] active:bg-[#1AA34C] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366] focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              <MessageCircle className="h-5 w-5" />
              Falar no WhatsApp
            </a>
            <a
              href="#contact"
              onClick={(e) => scrollToContact(e, "matrices_cta")}
              className="inline-flex items-center justify-center gap-2 border-2 border-foreground text-foreground font-heading font-semibold px-8 py-4 rounded-lg text-base transition-colors hover:bg-foreground hover:text-background active:bg-foreground/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              <FlaskConical className="h-5 w-5" />
              Enviar amostra para análise
            </a>
          </div>
        </div>
      </ScrollSection>

      <Suspense fallback={<div className="min-h-[200px]" />}>
        {/* Social Proof */}
        <ScrollSection>
          <SocialProof />
        </ScrollSection>

        {/* FAQ */}
        <ScrollSection>
          <FAQ />
        </ScrollSection>

        {/* Contact */}
        <ScrollSection as="section" id="contact" className="py-12 sm:py-20 bg-background scroll-mt-20">
        <div className="container mx-auto px-3 sm:px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8 sm:mb-10">
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">
                Vamos Trabalhar Juntos
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-3">
                Preencha o formulário e nosso time entrará em contato em 15 minutos.
              </p>
            </div>
            <div className="bg-card rounded-2xl border border-border p-4 sm:p-8 shadow-sm">
              <ContactForm />
            </div>
          </div>
        </div>
        </ScrollSection>
      </Suspense>

      </main>
      {/* Footer */}
      <footer className="bg-foreground py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
            {/* Brand & Info */}
            <div>
              <img src={logo} alt="GLP Laboratórios" className="h-8 mb-4 brightness-0 invert" />
              <p className="text-card/60 text-sm leading-relaxed max-w-sm">
                Laboratório especializado em ensaios de radiações ionizantes, atendendo empresas de todo o Brasil desde 2015.
              </p>
            </div>

            {/* Address & Map */}
            <div>
              <div className="flex items-start gap-2 mb-3">
                <MapPin className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="text-card/80 text-sm font-semibold font-heading">GLP Laboratórios</p>
                  <p className="text-card/60 text-sm">
                    Condomínio Industrial Alphanorth<br />
                    Rua Democlácio José Rossin, 112 — Guamium<br />
                    Piracicaba, SP — 13413-034
                  </p>
                </div>
              </div>
              <div className="rounded-lg overflow-hidden border border-card/10 mt-3">
                <iframe
                  title="Localização GLP Laboratórios"
                  src="https://www.google.com/maps?q=Rua%20Democl%C3%A1cio%20Jos%C3%A9%20Rossin%2C%20112%20-%20Guamium%2C%20Piracicaba%20-%20SP%2C%2013413-034&output=embed"
                  width="100%"
                  height="180"
                  style={{ border: 0 }}
                  allowFullScreen={false}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>

          <div className="border-t border-card/10 mt-8 pt-6 space-y-4">
            <div className="text-card/60 text-xs leading-relaxed max-w-3xl mx-auto text-center space-y-2">
              <p className="font-heading font-semibold text-card/80 text-sm">
                Proteção de dados — LGPD (Lei nº 13.709/2018)
              </p>
              <p>
                Ao enviar o formulário, você autoriza a GLP Laboratórios a tratar os
                dados informados (nome, e-mail, telefone, empresa e mensagem) com a
                finalidade exclusiva de responder à sua solicitação e manter contato
                comercial. Os dados podem ser armazenados em nosso CRM e ferramenta
                de e-mail marketing e são mantidos por até <strong>5 anos</strong>{" "}
                após o último contato.
              </p>
              <p>
                Você pode solicitar acesso, correção, portabilidade ou exclusão dos
                seus dados, bem como revogar o consentimento, a qualquer momento
                junto ao nosso Encarregado (DPO):{" "}
                <a
                  href="mailto:marketing@glplab.com.br"
                  className="underline hover:text-primary"
                >
                  Fabio — marketing@glplab.com.br
                </a>
                .
              </p>
            </div>
            <p className="text-card/50 text-sm text-center">
              © 2026 GLP Laboratórios. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
